import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, Upload, X, Image as ImageIcon, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['Bebés', 'Bodas', 'Primera Comunión', 'Embarazo', 'Publicidad', 'Familia'];

export const AdminGalleries = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, completed: [] });
  const [form, setForm] = useState({
    title: '', category: categories[0], description: '', date: '', 
    location: '', rating: 5, thumbnail: '', images: []
  });

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await adminFetch(`${API}/galleries`);
      const data = await response.json();
      setGalleries(data);
    } catch (error) {
      toast.error('Error al cargar galerías');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e, isThumbnail = false) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    // For thumbnail, only use first file
    if (isThumbnail) {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', files[0]);
      formData.append('section', 'galerias');
      if (form.title) {
        formData.append('subpath', form.title);
        formData.append('filename_hint', `${form.title}-thumb`);
      }

      try {
        const response = await adminFetch(`${API}/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        setForm(prev => ({ ...prev, thumbnail: data.url }));
        toast.success('Miniatura subida correctamente');
      } catch (error) {
        toast.error('Error al subir miniatura');
      } finally {
        setUploading(false);
      }
      return;
    }

    // Multiple file upload for gallery images
    setUploading(true);
    setUploadProgress({ current: 0, total: files.length, completed: [] });
    
    const uploadedUrls = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'galerias');
      if (form.title) {
        formData.append('subpath', form.title);
        formData.append('filename_hint', `${form.title}-${String(i + 1).padStart(3, '0')}`);
      }
      
      try {
        const response = await adminFetch(`${API}/admin/upload`, {
          method: 'POST',
          body: formData
        });
        
        if (!response.ok) throw new Error('Upload failed');
        
        const data = await response.json();
        uploadedUrls.push(data.url);
        
        setUploadProgress(prev => ({
          ...prev,
          current: i + 1,
          completed: [...prev.completed, file.name]
        }));
      } catch (error) {
        toast.error(`Error al subir: ${file.name}`);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setForm(prev => ({ ...prev, images: [...prev.images, ...uploadedUrls] }));
      toast.success(`${uploadedUrls.length} ${uploadedUrls.length === 1 ? 'imagen subida' : 'imágenes subidas'} correctamente`);
    }
    
    setUploading(false);
    setUploadProgress({ current: 0, total: 0, completed: [] });
    
    // Reset file input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const openModal = (gallery = null) => {
    if (gallery) {
      setEditingGallery(gallery);
      setForm({
        title: gallery.title,
        category: gallery.category,
        description: gallery.description,
        date: gallery.date,
        location: gallery.location,
        rating: gallery.rating,
        thumbnail: gallery.thumbnail,
        images: gallery.images || []
      });
    } else {
      setEditingGallery(null);
      setForm({
        title: '', category: categories[0], description: '', date: '', 
        location: '', rating: 5, thumbnail: '', images: []
      });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.title || !form.category) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    try {
      const url = editingGallery 
        ? `${API}/admin/galleries/${editingGallery.gallery_id}`
        : `${API}/admin/galleries`;
      
      const response = await adminFetch(url, {
        method: editingGallery ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });

      if (!response.ok) throw new Error('Error saving');

      toast.success(editingGallery ? 'Galería actualizada' : 'Galería creada');
      setShowModal(false);
      fetchGalleries();
    } catch (error) {
      toast.error('Error al guardar galería');
    }
  };

  const handleDelete = async (galleryId) => {
    if (!confirm('¿Eliminar esta galería?')) return;

    try {
      const response = await adminFetch(`${API}/admin/galleries/${galleryId}`, {
        method: 'DELETE'
      });

      if (!response.ok) throw new Error('Error deleting');

      toast.success('Galería eliminada');
      fetchGalleries();
    } catch (error) {
      toast.error('Error al eliminar galería');
    }
  };

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Galerías</h1>
            <p className="text-muted-foreground">Gestiona tus galerías de fotos</p>
          </div>
          <Button onClick={() => openModal()} className="gap-2">
            <Plus className="w-4 h-4" /> Nueva Galería
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map(i => <div key={i} className="h-64 bg-muted animate-pulse" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleries.map((gallery) => (
              <div key={gallery.gallery_id} className="bg-card border border-border overflow-hidden group">
                <div className="aspect-video relative">
                  {gallery.thumbnail ? (
                    <img src={gallery.thumbnail} alt={gallery.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-muted flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-muted-foreground" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <Button size="sm" variant="secondary" onClick={() => openModal(gallery)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => handleDelete(gallery.gallery_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-muted-foreground mb-1">{gallery.category}</p>
                  <h3 className="font-medium">{gallery.title}</h3>
                  <p className="text-sm text-muted-foreground">{gallery.images?.length || 0} fotos</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>

      {/* Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-background border border-border w-[42rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto"
          >
            <div className="p-6 border-b border-border flex items-center justify-between">
              <h2 className="font-serif text-xl">{editingGallery ? 'Editar Galería' : 'Nueva Galería'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Título *</label>
                  <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Categoría *</label>
                  <select 
                    value={form.category} 
                    onChange={e => setForm({...form, category: e.target.value})}
                    className="w-full h-10 px-3 border border-input bg-background rounded-md"
                  >
                    {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Descripción</label>
                <Textarea value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Fecha</label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Ubicación</label>
                  <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} />
                </div>
              </div>

              {/* Thumbnail */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">Miniatura</label>
                {form.thumbnail ? (
                  <div className="relative w-32 h-32">
                    <img src={form.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                    <button 
                      type="button"
                      onClick={() => setForm({...form, thumbnail: ''})}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-destructive text-white rounded-full flex items-center justify-center"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center justify-center w-32 h-32 border-2 border-dashed border-border cursor-pointer hover:border-foreground/50">
                    <input type="file" accept="image/*" className="hidden" onChange={e => handleUpload(e, true)} />
                    <Upload className="w-6 h-6 text-muted-foreground" />
                  </label>
                )}
              </div>

              {/* Images */}
              <div>
                <label className="text-xs uppercase tracking-widest text-muted-foreground block mb-2">
                  Imágenes de la galería
                  <span className="text-muted-foreground/60 normal-case ml-2">(puedes seleccionar varias a la vez)</span>
                </label>
                
                {/* Upload progress */}
                {uploading && uploadProgress.total > 0 && (
                  <div className="mb-4 p-4 bg-muted/50 border border-border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">
                        Subiendo {uploadProgress.current} de {uploadProgress.total}
                      </span>
                      <span className="text-sm text-muted-foreground">
                        {Math.round((uploadProgress.current / uploadProgress.total) * 100)}%
                      </span>
                    </div>
                    <div className="h-2 bg-muted rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full bg-foreground"
                        initial={{ width: 0 }}
                        animate={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    {uploadProgress.completed.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {uploadProgress.completed.slice(-3).map((name, i) => (
                          <span key={i} className="inline-flex items-center gap-1 text-xs text-green-500">
                            <CheckCircle className="w-3 h-3" /> {name.length > 15 ? name.slice(0, 15) + '...' : name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                <div className="flex flex-wrap gap-2">
                  {form.images.map((img, index) => (
                    <div key={index} className="relative w-20 h-20">
                      <img src={img} alt={`Image ${index}`} className="w-full h-full object-cover" />
                      <button 
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                  <label className={`flex flex-col items-center justify-center w-20 h-20 border-2 border-dashed border-border cursor-pointer hover:border-foreground/50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                    <input 
                      type="file" 
                      accept="image/*" 
                      multiple 
                      className="hidden" 
                      onChange={e => handleUpload(e, false)}
                      disabled={uploading}
                    />
                    {uploading && uploadProgress.total > 0 ? (
                      <div className="w-5 h-5 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Plus className="w-5 h-5 text-muted-foreground" />
                        <span className="text-[10px] text-muted-foreground mt-1">Añadir</span>
                      </>
                    )}
                  </label>
                </div>
                {form.images.length > 0 && (
                  <p className="text-xs text-muted-foreground mt-2">{form.images.length} {form.images.length === 1 ? 'imagen' : 'imágenes'} en la galería</p>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-border">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                <Button type="submit">{editingGallery ? 'Guardar Cambios' : 'Crear Galería'}</Button>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminGalleries;
